<template>
  <el-form
    class="profile-tab"
    :style="{ height: height, overflow: 'auto', 'padding-bottom': '50px' }"
    :key="attributesKey"
    data-qa="preview-user-profile-tab"
  >
    <el-form-item :label="$t('CampaignForm_Preview_Profile_Title')">
      <el-select
        v-model="profileId"
        :loading="loading"
        filterable
        remote
        @change="onChange"
        :remote-method="fetchProfiles"
        clearable
        @clear="fetchProfiles"
        data-qa="preview-user-profile-select"
      >
        <el-option-group
          v-if="seedProfiles.length"
          :label="$t('CampaignForm_Preview_Profile_Dropdown_SeedList')"
        >
          <el-option
            v-for="(item, index) in seedProfiles"
            :key="index"
            :label="
              item.email ||
                ($hasFeature('customerIdEnabled') ? item.customer_id : null) ||
                item.user_id ||
                item.id
            "
            :value="item.id"
          />
        </el-option-group>

        <el-option-group
          v-if="profiles.length"
          :label="$t('CampaignForm_Preview_Profile_Dropdown_AllProfiles')"
        >
          <el-option
            v-for="(item, index) in profiles"
            :key="index"
            :label="
              item.email ||
                ($hasFeature('customerIdEnabled') ? item.customer_id : null) ||
                item.user_id ||
                item.id
            "
            :value="item.id"
          />
        </el-option-group>
      </el-select>
    </el-form-item>

    <div
      v-if="regularAttributesCount > 0"
      class="attributes-section"
      data-qa="preview-user-profile-attributes"
    >
      <div class="section-header">
        {{ $t('CampaignForm_Preview_Attributes') }}
      </div>
      <form-item
        class="flexbox"
        v-for="(val, attr) in regularAttributes"
        :label="attr"
        :key="`attr-${attr}`"
        :data-qa="`preview-user-profile-attribute-${attr}`"
      >
        <el-tooltip
          :content="getUnmetTooltipMessage(attr)"
          placement="top"
          effect="light"
          :disabled="!hasUnmetAttributeConditions(attr)"
        >
          <el-input
            class="borderless"
            :class="getUnmetConditionsClass(attr)"
            v-model="attributes[attr]"
            placeholder="-"
            readonly
            :data-qa="`preview-user-profile-attribute-input-${attr}`"
          />
        </el-tooltip>
      </form-item>
    </div>

    <div
      v-if="subscriptionPreferencesEnabled && consentPreferencesCount > 0"
      class="preferences-section"
      data-qa="preview-user-profile-preferences"
    >
      <div class="section-header">
        {{ $t('CampaignForm_Preview_Preferences') }}
      </div>
      <form-item
        class="flexbox"
        v-for="(val, attr) in consentPreferences"
        :label="getConsentPreferenceLabel(attr)"
        :key="attr"
        :data-qa="`preview-user-profile-preference-${attr}`"
      >
        <el-tooltip
          :content="getUnmetTooltipMessage(attr)"
          placement="top"
          effect="light"
          :disabled="!hasUnmetAttributeConditions(attr)"
        >
          <el-input
            class="borderless preference-value"
            :class="getUnmetConditionsClass(attr)"
            :value="getConsentPreferenceValue(attr)"
            placeholder="-"
            readonly
            :data-qa="`preview-user-profile-preference-input-${attr}`"
          />
        </el-tooltip>
      </form-item>
    </div>
  </el-form>
</template>

<script>
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import { $hasFeature } from '@/core/utils/VueAcl'
import _ from 'lodash'
import { EventBus } from '@/core/EventBus'
import FormItem from '@/common/components/form/FormItem.vue'
import SubscriptionPreferencesMixin from './mixins/SubscriptionPreferencesMixin'

export default {
  name: 'PreviewContentUserProfileTab',
  components: { FormItem },
  mixins: [SubscriptionPreferencesMixin],
  props: {
    initialProfile: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    const seeded = this.initialProfile ? [this.initialProfile] : []
    return {
      height: '450px',
      attributes: [],
      profileId: this.initialProfile ? this.initialProfile.id : null,
      data: [],
      profiles: seeded,
      seedProfiles: [],
      loading: false,
      unmetConditions: []
    }
  },
  mounted() {
    this.calculateHeight()
    if (this.profileId) {
      this.$emit('data-change', this.profileId)
      this.$emit('profile-ready')
    }
    this.fetchProfiles(null, true)
    this.fetchCustomChannels()
    this.onSetProfileAttributes = attributes => {
      this.attributes = attributes
      this.checkAndFetchPreferences()
    }
    this.onSetUnmetConditions = unmetConditions => {
      this.unmetConditions = unmetConditions
    }
    EventBus.$on('set-profile-attributes', this.onSetProfileAttributes)
    EventBus.$on('set-unmet-attribute-conditions', this.onSetUnmetConditions)
  },
  computed: {
    seedListEnabled() {
      return $hasFeature('featureSeedList') && $hasFeature('seedListEnabled')
    },
    regularAttributes() {
      if (!this.attributes || typeof this.attributes !== 'object') {
        return {}
      }
      return Object.keys(this.attributes).reduce((regular, attr) => {
        if (!this.isConsentPreference(attr)) {
          regular[attr] = this.attributes[attr]
        }
        return regular
      }, {})
    },
    regularAttributesCount() {
      return Object.keys(this.regularAttributes).length
    },
    attributesKey() {
      if (!this.attributes || typeof this.attributes !== 'object') {
        return 0
      }
      return Object.keys(this.attributes).length
    }
  },
  methods: {
    getAttributes() {
      return this.attributes
    },
    fetchNonSeedListProfilesEndpoint(params = {}) {
      return projectEndpoint({
        method: 'get',
        url: `user-profiles`,
        params: { in_seed_list: 0, ...params },
        handler: data => {
          this.profiles = data
        }
      })
    },
    fetchSeedListProfilesEndpoint(params = {}) {
      if (!this.seedListEnabled) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `user-profiles`,
        params: { in_seed_list: 1, ...params },
        handler: data => {
          this.seedProfiles = data
        }
      })
    },
    onChange(profileId) {
      if (this.attributes && typeof this.attributes === 'object') {
        Object.keys(this.attributes).forEach(attr => {
          this.attributes[attr] = null
        })
      }
      this.subscriptionPreferences = {}
      this.preferenceMetadata = {}
      if (
        this.subscriptionPreferencesEnabled &&
        profileId &&
        this.consentPreferencesCount > 0
      ) {
        this.fetchSubscriptionPreferences(profileId)
      }
      const selected =
        this.seedProfiles.find(p => p.id === profileId) ||
        this.profiles.find(p => p.id === profileId) ||
        null
      this.$emit('profile-change', selected)
      this.$emit('data-change', profileId)
    },
    checkAndFetchPreferences() {
      if (!this.subscriptionPreferencesEnabled) return
      this.$nextTick(() => {
        if (this.profileId && this.consentPreferencesCount > 0) {
          this.fetchSubscriptionPreferences(this.profileId)
        }
      })
    },
    fetchProfiles(query, firstLoad = false) {
      let params = {}

      if (query && query.length === 32 && !query.includes('@')) {
        params.id = query
      } else if (query) {
        params.email = query
      }

      this.loading = true

      let promises = []
      let fetchEndpoints = [
        this.fetchNonSeedListProfilesEndpoint(params),
        this.fetchSeedListProfilesEndpoint(params)
      ]
      fetchEndpoints.forEach(endpoint => {
        if (endpoint) {
          promises.push(
            RestApi(endpoint)
              .then(({ data }) => {
                endpoint.handler(data)
              })
              .catch(error => {
                this.loading = false
                this.$message({
                  message: error.message,
                  type: 'error'
                })
              })
          )
        }
      })

      Promise.all(promises)
        .then(() => {
          if (firstLoad && !this.profileId) {
            this.profileId =
              _.get(this.seedProfiles, '[0].id') ||
              _.get(this.profiles, '[0].id') ||
              null

            if (this.profileId) {
              this.onChange(this.profileId)
            }
            this.$emit('profile-ready')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    calculateHeight() {
      // Calculate dynamic height for attributes section
      let height = 0
      let minHeight = 250
      if (!document.querySelector('.sidebar-ct')) {
        this.height = '450px'
      }

      let sidebarHeight = document.querySelector('.sidebar-ct').clientHeight
      let optionsHeight = 0
      document.querySelectorAll('.campaign-preview-option').forEach(el => {
        optionsHeight += el.clientHeight || 0
      })
      height = sidebarHeight - optionsHeight - 165

      this.height = Math.max(height, minHeight) + 'px'
    },
    isUnmetAttributeConditionWithFallback(attribute) {
      const list =
        this.unmetConditions && this.unmetConditions['unmetConditionAttributes']
      return Array.isArray(list) && list.includes(attribute)
    },
    isUnmetAttributeConditionWithNoFallback(attribute) {
      const list =
        this.unmetConditions &&
        this.unmetConditions['unmetConditionAttributesNoFallback']
      return Array.isArray(list) && list.includes(attribute)
    },
    hasUnmetAttributeConditions(attribute) {
      return (
        this.isUnmetAttributeConditionWithFallback(attribute) ||
        this.isUnmetAttributeConditionWithNoFallback(attribute)
      )
    },
    getUnmetConditionsClass(attribute) {
      return { 'unmet-conditions': this.hasUnmetAttributeConditions(attribute) }
    },
    getUnmetTooltipMessage(attribute) {
      if (this.isUnmetAttributeConditionWithFallback(attribute)) {
        return this.$t(
          'CampaignForm_Preview_Attribute_Condition_Fallback_Applied'
        )
      }
      if (this.isUnmetAttributeConditionWithNoFallback(attribute)) {
        return this.$t(
          'CampaignForm_Preview_Attribute_Condition_No_Fallback_Available'
        )
      }
    },
    getConsentPreferenceValue(attribute) {
      if (!this.isConsentPreference(attribute)) {
        return this.attributes[attribute] || '-'
      }

      const parsed = this.parseConsentPreferenceKey(attribute)
      if (!parsed || parsed.channel == null || !parsed.name) {
        return this.attributes[attribute] || '-'
      }

      const channelPrefs = this.subscriptionPreferences[parsed.channel]
      if (!channelPrefs) {
        return '-'
      }

      const status = channelPrefs[parsed.name]
      if (status == null) {
        return 'NULL'
      }
      return String(status)
    }
  },
  destroyed() {
    EventBus.$off('set-profile-attributes', this.onSetProfileAttributes)
    EventBus.$off('set-unmet-attribute-conditions', this.onSetUnmetConditions)
  }
}
</script>

<style scoped lang="scss">
.flexbox {
  display: flex;
  justify-content: space-between;
}

.flexbox:before,
.flexbox::after {
  display: inherit;
  content: none;
}

/deep/ .flexbox .el-form-item__label {
  font-size: 12px;
  font-weight: 600;
  padding-top: 4px;
}

/deep/ .borderless > .el-input__inner {
  border: 0;
  border-bottom: 1px solid #c2c7d3;
  border-radius: 0;
}

/deep/ .unmet-conditions > .el-input__inner {
  background-color: #fde8e9;
}

.section-header {
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  margin-bottom: 10px;
  color: #303133;
}

.attributes-section:first-of-type .section-header {
  margin-top: 0;
}

.preferences-section {
  margin-top: 20px;
}

.attributes-section + .preferences-section {
  padding-top: 3px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: #e4e7ed;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
}

/deep/ .preference-value {
  max-width: 70px;
}

/deep/ .preference-value > .el-input__inner {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/content-data-tabs/PreviewContentUserProfileTab.vue